import React from "react"
import styles from "./FvVideoPreview.module.scss"
import { IPlacementDataTable } from "../types/placement"
import { IVideoMetrics } from "../types/types"
import { Image, Tooltip } from "antd"

interface FvVideoPreviewProps {
  placement: IPlacementDataTable
}

const STATUS_LABELS: Record<string, string> = {
  APPROVED: "approved",
  PENDING: "in moderation",
  REJECTED: "rejected",
}

const STATUS_STYLE: Record<string, string> = {
  APPROVED: styles.statusApproved,
  PENDING: styles.statusPending,
  REJECTED: styles.statusRejected,
}

const resolveStatus = (status?: string): { label: string; className?: string } => {
  if (!status) {
    return { label: "" }
  }
  return {
    label: STATUS_LABELS[status] ?? status.toLowerCase(),
    className: STATUS_STYLE[status],
  }
}

const TOOLTIP_INNER_STYLE = {
  padding: 0,
  background: "transparent",
  boxShadow: "none",
  borderRadius: 4,
  lineHeight: 0,
}

const formatRatePercent = (ratio: number): string => `${(ratio * 100).toFixed(1)}%`

const formatCount = (value: number): string => value.toLocaleString("en-US")

const renderCover = (coverUrl?: string) => {
  if (coverUrl) {
    return <Image src={coverUrl} alt="featured video cover" className={styles.cover} />
  }
  return <span className={styles.emptyCover}>no asset</span>
}

const EngagementTooltip: React.FC<{ metrics: IVideoMetrics }> = ({ metrics }) => (
  <div>
    <div>5s view rate: {formatRatePercent(metrics.video5SecondViewRate)}</div>
    <div>5s views: {formatCount(metrics.video5SecondViews)}</div>
    <div>Complete views: {formatCount(metrics.videoCompleteViews)}</div>
    <div>Viewable impressions: {formatCount(metrics.viewableImpressions)}</div>
  </div>
)

const renderEngagement = (metrics?: IVideoMetrics) => {
  if (!metrics) {
    return null
  }
  return (
    <div className={styles.engagement}>
      5s view rate {formatRatePercent(metrics.video5SecondViewRate)}
    </div>
  )
}

const FvVideoPreview: React.FC<FvVideoPreviewProps> = ({ placement }) => {
  const status = resolveStatus(placement.extensionModerationStatus)
  const metrics = placement.videoMetrics

  const content = (
    <span className={styles.thumbnail}>
      {renderCover(placement.renderedCoverImageUrl)}
      <span>
        <div className={styles.label}>Featured Videos</div>
        {status.label && (
          <div className={`${styles.status} ${status.className ?? ""}`}>{status.label}</div>
        )}
        {renderEngagement(metrics)}
      </span>
    </span>
  )

  if (!metrics) {
    return content
  }

  return (
    <Tooltip title={<EngagementTooltip metrics={metrics} />} mouseEnterDelay={0.2}>
      {content}
    </Tooltip>
  )
}

export default FvVideoPreview
